import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import './TeacherPrepView.css';
import './TeacherDetailPages.css';

function TeacherPrepView({ teacherData }) {
  const [lessonPlans, setLessonPlans] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [newPlan, setNewPlan] = useState({
    title: '',
    subject: '',
    classId: '',
    description: '',
    attachments: []
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const loadLessonPlans = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    console.log('🔥 TeacherPrep: Loading teacher preps for teacher', teacherData.uid);
    try {
      // Load from classes/{classId}/teacherPreps subcollection (like iOS)
      const allPreps = [];
      for (const cls of classes) {
        const plansQuery = query(
          collection(db, 'classes', cls.id, 'teacherPreps')
        );
        const snapshot = await getDocs(plansQuery);
        const plansData = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data(), classId: cls.id }))
          .filter(p => p.isActive !== false && (!p.teacherId || p.teacherId === teacherData.uid));
        allPreps.push(...plansData);
      }
      console.log('🔥 TeacherPrep: Teacher preps loaded', allPreps.length, allPreps);
      setLessonPlans(allPreps);
    } catch (error) {
      console.error('🔥 TeacherPrep: Error loading teacher preps:', error);
    } finally {
      setLoading(false);
    }
  }, [classes, teacherData]);

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) return;
    console.log('🔥 TeacherPrep: Loading classes for teacher', teacherData.uid);
    console.log('🔥 TeacherPrep: Teacher data', teacherData);
    try {
      // First, load ALL classes to see what's in the database
      console.log('🔥 TeacherPrep: Loading ALL classes for debugging');
      const allClassesQuery = query(collection(db, 'classes'));
      const allClassesSnapshot = await getDocs(allClassesQuery);
      const allClassesData = allClassesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherPrep: ALL classes in database', allClassesData.length, allClassesData);
      console.log('🔥 TeacherPrep: Class teacherIds', allClassesData.map(c => ({ id: c.id, name: c.name, teacherId: c.teacherId })));

      // Load classes for this teacher
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('🔥 TeacherPrep: Classes loaded for teacher', classesData.length, classesData);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 TeacherPrep: Error loading classes:', error);
    }
  }, [teacherData]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadLessonPlans();
    }
  }, [classes, loadLessonPlans]);

  const handleAddPlan = async (e) => {
    e.preventDefault();
    try {
      if (!newPlan.classId) {
        alert('Please select a class');
        return;
      }
      const attachmentURLs = newPlan.attachments.map(a => a.url);
      const attachmentNames = newPlan.attachments.map(a => a.name);
      await addDoc(collection(db, 'classes', newPlan.classId, 'teacherPreps'), {
        title: newPlan.title,
        subject: newPlan.subject,
        description: newPlan.description,
        teacherId: teacherData?.uid,
        isActive: true,
        status: 'draft',
        createdAt: new Date().toISOString(),
        schoolId: teacherData?.schoolId || null,
        attachmentURLs,
        attachmentNames
      });
      setShowAddForm(false);
      setNewPlan({ title: '', subject: '', classId: '', description: '', attachments: [] });
      loadLessonPlans();
    } catch (error) {
      console.error('Error adding lesson plan:', error);
      alert('Failed to add lesson plan');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setUploadError('');
    try {
      const uploaded = [];
      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        const isPDF = file.type === 'application/pdf';
        if (!isImage && !isPDF) {
          setUploadError('Only images and PDF files are allowed');
          continue;
        }
        const fileName = `${Date.now()}_${file.name.replace(/\s/g, '_')}`;
        const path = isImage ? `prep_attachments/${fileName}` : `prep_attachments/${fileName}`;
        const storageRef = ref(storage, path);
        const metadata = { contentType: file.type };
        await uploadBytes(storageRef, file, metadata);
        const downloadURL = await getDownloadURL(storageRef);
        uploaded.push({ url: downloadURL, name: file.name });
      }
      setNewPlan(prev => ({ ...prev, attachments: [...prev.attachments, ...uploaded] }));
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadError('Failed to upload file: ' + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setNewPlan(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleDeleteAttachment = async (plan, attachmentIndex) => {
    const url = plan.attachmentURLs?.[attachmentIndex];
    if (!url) return;
    try {
      const storageRef = ref(storage, url);
      await deleteObject(storageRef);
      const newURLs = (plan.attachmentURLs || []).filter((_, i) => i !== attachmentIndex);
      const newNames = (plan.attachmentNames || []).filter((_, i) => i !== attachmentIndex);
      await updateDoc(doc(db, 'classes', plan.classId, 'teacherPreps', plan.id), {
        attachmentURLs: newURLs,
        attachmentNames: newNames
      });
      loadLessonPlans();
    } catch (error) {
      console.error('Error deleting attachment:', error);
      alert('Failed to delete attachment');
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!confirm('Are you sure you want to delete this lesson plan?')) return;
    try {
      await updateDoc(doc(db, 'classes', plan.classId, 'teacherPreps', plan.id), { isActive: false });
      loadLessonPlans();
    } catch (error) {
      console.error('Error deleting lesson plan:', error);
      alert('Failed to delete lesson plan');
    }
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Full-screen lesson plan detail page (replaces the old cramped modal)
  if (showDetail && selectedPlan) {
    return (
      <div className="detail-page">
        <button className="detail-back-button" onClick={() => setShowDetail(false)}>
          ◄ Back to Lesson Plans
        </button>
        <div className="detail-hero">
          <div className="detail-avatar">📚</div>
          <div className="detail-hero-info">
            <h2>{selectedPlan.title}</h2>
            <p className="detail-subtitle">{getClassName(selectedPlan.classId)}</p>
            <div className="detail-badges">
              <span className={`detail-badge ${selectedPlan.status === 'published' ? 'green' : 'gray'}`}>
                {selectedPlan.status || 'draft'}
              </span>
              {selectedPlan.subject && <span className="detail-badge">{selectedPlan.subject}</span>}
              {selectedPlan.isActive === false && <span className="detail-badge red">Inactive</span>}
            </div>
          </div>
          <div className="detail-hero-actions">
            <button
              className="detail-btn detail-btn-delete"
              onClick={() => {
                handleDeletePlan(selectedPlan);
                setShowDetail(false);
              }}
            >
              🗑️ Delete Lesson Plan
            </button>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-card">
            <h3>Plan Details</h3>
            <div className="detail-field"><span className="field-label">Subject</span><span className="field-value">{selectedPlan.subject || 'N/A'}</span></div>
            <div className="detail-field"><span className="field-label">Class</span><span className="field-value">{getClassName(selectedPlan.classId)}</span></div>
            <div className="detail-field"><span className="field-label">Status</span><span className="field-value">{selectedPlan.status || 'draft'}</span></div>
            <div className="detail-field"><span className="field-label">Created</span><span className="field-value">{selectedPlan.createdAt ? new Date(selectedPlan.createdAt).toLocaleDateString() : 'N/A'}</span></div>
          </div>

          <div className="detail-card">
            <h3>Description</h3>
            {selectedPlan.description
              ? <p className="detail-empty" style={{ color: '#374151' }}>{selectedPlan.description}</p>
              : <p className="detail-empty">No description provided</p>}
          </div>

          <div className="detail-card full-width">
            <h3>Attachments ({(selectedPlan.attachmentURLs || []).length})</h3>
            {(!selectedPlan.attachmentURLs || selectedPlan.attachmentURLs.length === 0) ? (
              <p className="detail-empty">No attachments</p>
            ) : (
              <div className="detail-attachments">
                {selectedPlan.attachmentURLs.map((url, idx) => {
                  const name = selectedPlan.attachmentNames?.[idx] || 'Attachment';
                  const isImage = url.match(/\.(jpg|jpeg|png|gif|webp|heic)/i);
                  return (
                    <div key={idx} className="detail-attachment">
                      {isImage ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt={name} />
                        </a>
                      ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="attachment-link">
                          📄 {name}
                        </a>
                      )}
                      <button
                        className="attachment-remove"
                        title="Remove attachment"
                        onClick={async () => {
                          await handleDeleteAttachment(selectedPlan, idx);
                          setSelectedPlan(prev => prev ? {
                            ...prev,
                            attachmentURLs: (prev.attachmentURLs || []).filter((_, i) => i !== idx),
                            attachmentNames: (prev.attachmentNames || []).filter((_, i) => i !== idx)
                          } : prev);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-prep-view">
      <div className="view-header">
        <h2>Teacher Prep</h2>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Lesson Plan
        </button>
      </div>

      <p className="count-text">Lesson Plans: {lessonPlans.length}</p>

      {showAddForm && (
        <div className="add-plan-form">
          <h3>Create New Lesson Plan</h3>
          <form onSubmit={handleAddPlan}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={newPlan.title}
                onChange={(e) => setNewPlan({ ...newPlan, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Subject *</label>
              <input
                type="text"
                value={newPlan.subject}
                onChange={(e) => setNewPlan({ ...newPlan, subject: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Class *</label>
              <select
                value={newPlan.classId}
                onChange={(e) => setNewPlan({ ...newPlan, classId: e.target.value })}
                required
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} ({cls.grade})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newPlan.description}
                onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Attachments (Photos / PDFs)</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                ref={fileInputRef}
                onChange={handleFileUpload}
                style={{ marginBottom: '8px' }}
              />
              {uploading && <p className="uploading-text">Uploading...</p>}
              {uploadError && <p className="error-text">{uploadError}</p>}
              {newPlan.attachments.length > 0 && (
                <div className="attachment-list">
                  {newPlan.attachments.map((att, idx) => (
                    <div key={idx} className="attachment-item">
                      <span>{att.name}</span>
                      <button type="button" onClick={() => handleRemoveAttachment(idx)}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Plan</button>
            </div>
          </form>
        </div>
      )}

      {lessonPlans.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📚</div>
          <h3>No Lesson Plans</h3>
          <p>Create your first lesson plan to get started</p>
        </div>
      ) : (
        <div className="plans-list">
          {lessonPlans.map(plan => (
            <div
              key={plan.id}
              className="plan-card"
              onClick={() => {
                setSelectedPlan(plan);
                setShowDetail(true);
              }}
            >
              <div className="plan-icon">📚</div>
              <div className="plan-info">
                <h4>{plan.title}</h4>
                <p>Subject: {plan.subject}</p>
                <p>Class: {getClassName(plan.classId)}</p>
              </div>
              <div className="plan-status">
                <span className="status-badge">{plan.status}</span>
              </div>
              <button
                className="delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePlan(plan);
                }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default TeacherPrepView;
